<template>
  <simple-modal
    id="room-detail-modal"
    :classes="modalClass"
    :class="isLoading ? 'loading-modal' : ''"
    @opened="opened"
    clickToClose
    height="auto"
    name="room-detail-modal"
  >
    <div v-if="isLoading" class="loading-wrapper">
      <img width="300" src="@/assets/rentalios.white.loader.gif" />
    </div>
    <div v-else-if="room" class="room-wrapper">
      <div class="room-header">
        <div class="room-internal-name">Room {{ room.internalName }}</div>
        <div class="room-marketing-name">
          {{ room.marketingName }}
        </div>
      </div>
      <div class="room-meta">
        <div class="room-property-internal-name">
          <img
            class="home-icon"
            alt="home"
            :src="require('@/assets/green-home.svg')"
          />
          {{ roomProperty.internalName }}
        </div>
        <div class="room-property-internal-name">
          <img
            class="home-icon"
            alt="home"
            :src="require('@/assets/editGreen.svg')"
          />
          Channel manager id: {{ roomProperty.uplistingId }}
        </div>
      </div>
      <div v-if="room.dynamicPricing" class="room-dynamic-pricing-wrapper">
        <div class="smart-pricing-wrapper">
          <div class="sub-title smart-pricing-title">Smart Pricing:</div>
          <div class="pricing-detail pricing-detail--inline">
            <div class="price-info">
              <img
                class="home-icon"
                alt="price"
                :src="require('@/assets/green-price.svg')"
              />
              Minimum: ${{ room.minPrice }}
            </div>
            <div class="price-info">
              <img
                class="home-icon"
                alt="price"
                :src="require('@/assets/green-price.svg')"
              />
              Maximum: ${{ room.maxPrice }}
            </div>
            <div class="price-info">
              <img
                class="home-icon"
                alt="price"
                :src="require('@/assets/green-price.svg')"
              />
              Automatic Discount: ${{ room.autoAdjustmentPercentage }}
            </div>
          </div>
          <div v-if="adjustments" class="seasonal-adjustments">
            <div class="sub-title">Seasonal Adjustments</div>
            <div
              class="pricing-detail pricing-detail--adjustment"
              v-for="(adjustment, index) in adjustments"
              :key="'adjustment-' + index"
            >
              <div class="price-info adjustment-name">
                <img
                  class="home-icon"
                  alt="price"
                  :src="require('@/assets/calendar-green.svg')"
                />
                {{ adjustment.name }}
              </div>
              <div class="price-info">
                {{ adjustment.type }} {{ adjustment.quantity
                }}{{ adjustmentSelect(adjustment) }}
              </div>
            </div>
          </div>
          <div class="graphic-title-wrapper">
            <div class="sub-title">Historical Graph</div>
            <div class="date-range">
              {{ rangeDates }}
            </div>
          </div>
          <div class="graph-legend-wrapper">
            <div class="legend-wrapper">
              <img alt="location icon" src="../../assets/location_icon.svg" />
              <span class="address">
                {{ address }}
              </span>
            </div>
            <div class="legend-wrapper legend-wrapper--items">
              <div class="estimated-rent-legend">Estimated Rent</div>
              <div class="last-year-rent-legend">Last Year Rent</div>
              <div class="actual-rent-legend">Actual Rent</div>
              <div class="average-rent-legend">Average Rent</div>
            </div>
          </div>
          <div
            class="graphic-wrapper actual-rent-chart-wrapper"
            @mouseleave="clearHistoricalChartHoverState"
          >
            <line-chart
              :chart-data="dataCollection"
              :options="chartOptions"
              style="background-color: #fcfcfc"
            ></line-chart>
            <actual-rent-tooltip
              :visible="historicalChartTooltip.visible"
              :mode="historicalChartTooltip.mode"
              :left="historicalChartTooltip.left"
              :top="historicalChartTooltip.top"
              :item="historicalChartTooltip.item"
              :line-label="historicalChartTooltip.lineLabel"
              :line-value="historicalChartTooltip.lineValue"
              :date-label="historicalChartTooltip.dateLabel"
            />
          </div>
        </div>
      </div>
      <div v-else class="room-property-internal-name">
        <img
          class="home-icon"
          alt="price"
          :src="require('@/assets/green-price.svg')"
        />
        Nightly Rent: {{ room.price }}
      </div>
      <div class="modal-footer">
        <div class="btn-wrapper">
          <button class="edit-btn" @click="toEdit">Edit</button>
          <button class="close-btn" @click="closeModal">Close</button>
        </div>
      </div>
    </div>
  </simple-modal>
</template>

<script>
import calls from '@/api/calls'
import LineChart from './LineChart.js'
import ActualRentTooltip from './ActualRentTooltip.vue'
import {
  HISTORICAL_CHART_BASE_OPTIONS,
  historicalChartHoverMixin
} from './historicalChartHover.js'

const PRICING_MONTHS_BACK = 6
const PRICING_MONTHS_AHEAD = 6

export default {
  name: 'RoomDetail',
  mixins: [historicalChartHoverMixin],
  props: {
    roomId: {
      type: String,
      default: ''
    }
  },
  components: {
    LineChart,
    ActualRentTooltip
  },
  data() {
    return {
      room: null,
      roomProperty: null,
      adjustments: null,
      isLoading: true,
      isDynamic: false,
      chartOptions: HISTORICAL_CHART_BASE_OPTIONS,
      modalClass: 'small-modal',
      dataCollection: null,
      rangeDates: null,
      averagePriceArray: [],
      maxPriceArray: [],
      minPriceArray: [],
      datesPriceArray: [],
      actualRentArray: [],
      lastYearActualRentArray: [],
      averageConstantArray: [],
      minPrice: '$',
      maxPrice: '$',
      averageConstant: 0
    }
  },
  computed: {
    address() {
      if (this.roomProperty) {
        return `${this.roomProperty.zipcode}, ${this.roomProperty.address}, ${this.roomProperty.state}`
      }
      return ''
    }
  },
  methods: {
    closeModal() {
      this.isLoading = true
      this.$modal.hide('room-detail-modal')
      this.modalClass = 'small-modal'
      this.dataCollection = null
    },
    toEdit() {
      this.$router.push({
        name: 'EditRoom',
        params: {
          roomId: this.room.uuid
        }
      })
    },
    adjustmentSelect(adjustment) {
      if (adjustment.select === 'Amount') {
        return '$'
      }
      return '%'
    },
    calculateDates(date) {
      let startDate = new Date(date[0])
      let endDate = new Date(date[1])
      this.rangeDates = `${startDate.toLocaleDateString(
        'en-US'
      )} - ${endDate.toLocaleDateString('en-US')}`
    },
    getDate() {
      const d = new Date()
      const month = String(d.getMonth() + 1).padStart(2, '0')
      const day = String(d.getDate()).padStart(2, '0')
      return [month, day, d.getFullYear()].join('/')
    },
    getPricingDateRange() {
      const startDate = new Date()
      startDate.setMonth(startDate.getMonth() - PRICING_MONTHS_BACK)
      const endDate = new Date()
      endDate.setMonth(endDate.getMonth() + PRICING_MONTHS_AHEAD)
      return { startDate, endDate }
    },
    formatPrice(price) {
      return String(price || '')
        .replace('$', '')
        .replace('%', '')
    },
    async getSeasonalPricing(adjustment) {
      let fixedMinPrice = parseInt(this.formatPrice(this.minPrice))
      let fixedMaxPrice = parseInt(this.formatPrice(this.maxPrice))
      fixedMinPrice = Math.round(fixedMinPrice)
      fixedMaxPrice = Math.round(fixedMaxPrice)

      const { data } = await calls.GETPRICING(
        parseInt(fixedMinPrice),
        parseInt(fixedMaxPrice),
        adjustment.startDate,
        adjustment.endDate,
        this.roomProperty.uuid,
        this.room.uuid,
        true
      )
      return { seasonDates: data.date, seasonPrices: data.Avg }
    },
    async getPricingData() {
      this._renderDataId = (this._renderDataId || 0) + 1
      const renderId = this._renderDataId

      this.averagePriceArray = []
      this.minPriceArray = []
      this.maxPriceArray = []
      this.datesPriceArray = []
      this.lastYearActualRentArray = []
      this.averageConstantArray = []

      const { startDate, endDate } = this.getPricingDateRange()
      this.calculateDates([startDate, endDate])

      const minPrice = this.formatPrice(this.minPrice)
      const maxPrice = this.formatPrice(this.maxPrice)

      if (minPrice.length <= 0 || maxPrice.length <= 0) return

      await new Promise((r) => setTimeout(r, 500))
      if (renderId !== this._renderDataId) return

      // Get dynamic pricing for all dates (without seasonal adjustments)
      const { data } = await calls.GETPRICING(
        this.formatPrice(this.minPrice),
        this.formatPrice(this.maxPrice),
        startDate.toLocaleDateString('en-US'),
        endDate.toLocaleDateString('en-US'),
        this.roomProperty.uuid,
        this.room.uuid,
        true
      )
      if (renderId !== this._renderDataId) return

      // Show the actual rent's price on graph
      await this.getActualRent()
      if (renderId !== this._renderDataId) return

      const lastYearRent = data.LastYearActualRent || []
      for (let i = 0; i < data.Avg.length; i++) {
        this.datesPriceArray.push(data.date[i])

        let predictionFormula = data.Avg[i]
        predictionFormula = Math.round(predictionFormula * 100) / 100
        if (predictionFormula > maxPrice) predictionFormula = maxPrice
        else if (predictionFormula < minPrice) predictionFormula = minPrice

        this.averagePriceArray.push(predictionFormula)
        const lastYearVal = lastYearRent[i]
        this.lastYearActualRentArray.push(
          lastYearVal != null
            ? Math.round(Number(lastYearVal) * 100) / 100
            : null
        )
      }

      // Seasonal adjustments
      for (const adjustIndex in this.adjustments) {
        const adjust = this.adjustments[adjustIndex]
        // Get pricing for this seasonal adjustment
        let { seasonDates, seasonPrices } = await this.getSeasonalPricing(
          adjust
        )
        if (renderId !== this._renderDataId) return
        for (let i = 0; i < seasonDates.length; i++) {
          const dateIndex = this.datesPriceArray.indexOf(seasonDates[i])
          let predictionFormula = seasonPrices[i]
          predictionFormula = Math.round(predictionFormula * 100) / 100
          if (predictionFormula > maxPrice) predictionFormula = maxPrice
          else if (predictionFormula < minPrice) predictionFormula = minPrice
          this.averagePriceArray[dateIndex] = predictionFormula
        }
      }

      // Calculate average value
      let averageSum = this.averagePriceArray.reduce(
        (partialSum, a) => partialSum + a,
        0
      )
      this.averageConstant = averageSum / data.Avg.length
      this.averageConstant = Math.round(this.averageConstant * 100) / 100

      for (let i = 0; i < data.Avg.length; i++) {
        this.averageConstantArray.push(this.averageConstant)
      }

      // Sort by date so x-axis is always chronological
      const combined = this.datesPriceArray.map((date, i) => ({
        date,
        avg: this.averagePriceArray[i],
        lastYear: this.lastYearActualRentArray[i],
        constant: this.averageConstantArray[i]
      }))
      combined.sort(
        (a, b) => new Date(a.date).getTime() - new Date(b.date).getTime()
      )
      this.datesPriceArray = combined.map((x) => x.date)
      this.averagePriceArray = combined.map((x) => x.avg)
      this.lastYearActualRentArray = combined.map((x) => x.lastYear)
      this.averageConstantArray = combined.map((x) => x.constant)

      await new Promise((r) => setTimeout(r, 500))
      if (renderId !== this._renderDataId) return
      this.fillData()
    },
    async getActualRent() {
      const { startDate, endDate } = this.getPricingDateRange()
      const startStr = startDate.toLocaleDateString('en-US')
      const endStr = endDate.toLocaleDateString('en-US')
      const result = await calls.GETACTUALRENT(this.room.uuid, startStr, endStr)
      this.actualRentArray = Array.isArray(result)
        ? result
        : (result && result.data) || []
    },
    fillData() {
      let pointRadius = []
      const today = this.getDate()
      this.datesPriceArray.forEach(function (eachDate) {
        if (eachDate == today) {
          pointRadius.push(5)
        } else {
          pointRadius.push(1)
        }
      })

      // Map actual rent [{ date, value }] to values aligned with chart labels by date
      const actualRentByTime = new Map()
      ;(this.actualRentArray || []).forEach((item) => {
        if (item && item.date != null) {
          const t = this.toTimeKey(item.date)
          if (t != null) actualRentByTime.set(t, item)
        }
      })
      this.actualRentByTime = actualRentByTime
      const actualRentData = this.datesPriceArray.map((label) => {
        const t = this.toTimeKey(label)
        if (t == null) return null
        const item = actualRentByTime.get(t)
        return item ? item.value : null
      })

      this.dataCollection = {
        labels: this.datesPriceArray,
        datasets: [
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Estimated Rent',
            fill: false,
            borderColor: '#66B015',
            data: this.averagePriceArray,
            yAxisID: 'y',
            backgroundColor: '#66B015',
            pointRadius: pointRadius
          }),
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Last Year Rent',
            fill: false,
            borderColor: '#5987EA',
            data: this.lastYearActualRentArray,
            yAxisID: 'y',
            backgroundColor: '#5987EA',
            pointRadius: 1
          }),
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Actual Rent',
            fill: true,
            borderColor: '#0B530E',
            data: actualRentData,
            yAxisID: 'y',
            backgroundColor: '#0B530E',
            pointRadius: 0
          }),
          this.applyHistoricalChartDatasetHoverDefaults({
            label: 'Average Rent',
            fill: false,
            borderColor: '#9F9F9F',
            data: this.averageConstantArray,
            yAxisID: 'y',
            pointRadius: 1
          })
        ]
      }
    },
    applyRoomPricingState() {
      this.isDynamic = this.room.dynamicPricing
      if (this.isDynamic) {
        this.minPrice =
          this.room.minPrice != null ? `$${this.room.minPrice}` : '$'
        this.maxPrice =
          this.room.maxPrice != null ? `$${this.room.maxPrice}` : '$'
      }
    },
    async getRoomData() {
      const { data } = await calls.GET('rooms', this.roomId)
      this.room = data.room || data
      this.adjustments = data.adjustments || []
      this.applyRoomPricingState()
    },
    async getPropertyData() {
      const { data } = await calls.GET('rooms/property', this.roomId)
      this.roomProperty = data.room || data
    },
    async opened() {
      this.room = null
      this.adjustments = []
      this.isDynamic = false
      this.clearHistoricalChartHoverState()
      this.chartOptions = this.buildHistoricalChartOptions()
      await this.getRoomData()
      await this.getPropertyData()
      if (this.isDynamic) {
        await this.getPricingData()
        this.modalClass = 'big-modal'
      }
      this.isLoading = false
    }
  }
}
</script>

<style scoped>
.room-wrapper {
  text-align: left;
  margin: 24px 24px 0;
  font-style: normal;
  position: relative;
  width: calc(100% - 48px);
  display: flex;
  flex-direction: column;
}

.room-header {
  margin-bottom: 12px;
}

.room-meta {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.room-meta .room-property-internal-name {
  margin: 0;
}

.room-internal-name {
  font-weight: 900;
  font-size: 16px;
  color: #66b015;
  margin: 0 0 4px;
}

.room-marketing-name {
  font-weight: 400;
  font-size: 12px;
  margin: 0;
  color: #4a4a4a;
}

.room-property-internal-name {
  display: flex;
  align-items: center;
  font-weight: 400;
  font-size: 12px;
  color: #000000;
  gap: 7px;
  margin: 0 0 16px;
  flex-wrap: wrap;
}

.home-icon {
  width: 14px;
  height: 14px;
  flex-shrink: 0;
}

.room-dynamic-pricing-wrapper {
  display: flex;
  flex-direction: column;
  gap: 0;
}

.smart-pricing-wrapper {
  display: flex;
  flex-direction: column;
}

.smart-pricing-title {
  margin-bottom: 8px;
}

.seasonal-adjustments {
  margin-top: 8px;
}

.seasonal-adjustments > .sub-title {
  margin-bottom: 8px;
}

.modal-footer {
  position: sticky;
  bottom: 0;
  background: white;
  border-top: 1px solid #cbd4dc;
  margin-top: 16px;
}

.btn-wrapper {
  padding-top: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 7px;
  padding-bottom: 24px;
}

.close-btn {
  padding: 7px 12px;
  width: 120px;
  height: 30px;
  background-color: #66b015;
  border: 1px solid #66b015;
  border-radius: 4px;
  color: #ffffff;
  cursor: pointer;
  font-size: 15px;
}

.edit-btn {
  padding: 7px 12px;
  width: 120px;
  height: 30px;
  background-color: #ffffff;
  border: 1px solid #66b015;
  border-radius: 4px;
  color: #66b015;
  cursor: pointer;
  font-size: 15px;
}

.sub-title {
  font-weight: 900;
  font-size: 12px;
  color: #000000;
}

.pricing-detail {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  font-weight: 400;
  font-size: 12px;
  color: #000000;
  margin: 0 0 16px;
}

.pricing-detail--inline {
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
}

.pricing-detail--adjustment {
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-bottom: 8px;
}

.pricing-detail--adjustment:last-child {
  margin-bottom: 16px;
}

.price-info {
  display: flex;
  gap: 7px;
  align-items: center;
}

.adjustment-name {
  min-width: 0;
  flex: none;
}

.graphic-title-wrapper {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 4px;
  margin-top: 8px;
  margin-bottom: 4px;
}

.date-range {
  font-weight: 500;
  font-size: 10px;
  color: #414850;
}

.address {
  font-weight: 500;
  font-size: 10px;
  color: #232a35;
}

.legend-wrapper {
  display: flex;
  align-items: center;
  gap: 7px;
  margin: 0;
}

.legend-wrapper--items {
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: 6px;
}

.graph-legend-wrapper {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 12px;
  margin: 12px 0 8px;
}

.graphic-wrapper {
  width: 100%;
  margin-bottom: 8px;
}

.estimated-rent-legend {
  background-color: #66b015;
  border-radius: 3px;
  color: #ffffff;
  font-size: 9px;
  padding: 8px;
  width: 85px !important;
  text-align: center;
}

.last-year-rent-legend {
  background-color: #5987ea;
  border-radius: 3px;
  color: #ffffff;
  font-size: 9px;
  padding: 8px;
  width: 70px;
  text-align: center;
}

.actual-rent-legend {
  background-color: #0b530e;
  border-radius: 3px;
  color: #ffffff;
  font-size: 9px;
  padding: 8px;
  width: 70px;
  text-align: center;
}

.average-rent-legend {
  background-color: #9f9f9f;
  border-radius: 3px;
  color: #ffffff;
  font-size: 9px;
  padding: 8px;
  width: 70px;
  text-align: center;
}

.actual-rent-chart-wrapper {
  position: relative;
}

.loading-modal >>> .vm--modal {
  background-color: transparent;
  box-shadow: none;
}

.loading-wrapper {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 120px;
}

::v-deep .big-modal {
  width: 70% !important;
  height: 85% !important;
  left: 22% !important;
  top: 5% !important;
  overflow: scroll;
}

/* Mobile Responsive Styles */
@media (max-width: 768px) {
  .room-wrapper {
    margin: 50px 16px 0;
    width: calc(100% - 32px);
    padding: 0 8px;
  }

  .room-header {
    margin-bottom: 16px;
  }

  .room-meta {
    gap: 12px;
    margin-bottom: 20px;
  }

  .room-internal-name {
    font-size: 18px;
    line-height: 24px;
    margin-bottom: 6px;
  }

  .room-marketing-name {
    font-size: 14px;
    line-height: 18px;
  }

  .room-property-internal-name {
    font-size: 14px;
    margin: 0;
    gap: 10px;
    flex-wrap: wrap;
  }

  .home-icon {
    width: 16px;
    height: 16px;
  }

  .sub-title {
    font-size: 14px;
  }

  .smart-pricing-title {
    margin-bottom: 10px;
  }

  .pricing-detail {
    margin-bottom: 16px;
  }

  .pricing-detail--inline {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .pricing-detail--adjustment {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
    margin-bottom: 12px;
  }

  .adjustment-name {
    min-width: 0;
    flex: none;
    width: 100%;
  }

  .price-info {
    font-size: 14px;
    gap: 10px;
    align-items: center;
  }

  .graphic-title-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 6px;
    margin-top: 12px;
    margin-bottom: 8px;
  }

  .date-range {
    font-size: 12px;
  }

  .graph-legend-wrapper {
    flex-direction: column;
    align-items: flex-start;
    gap: 10px;
    margin: 12px 0;
  }

  .legend-wrapper--items {
    justify-content: flex-start;
    gap: 8px;
  }

  .address {
    font-size: 12px;
  }

  .estimated-rent-legend,
  .last-year-rent-legend,
  .actual-rent-legend,
  .average-rent-legend {
    font-size: 11px;
    padding: 10px;
    width: 80px;
  }

  .graphic-wrapper {
    margin-bottom: 16px;
  }

  .modal-footer {
    padding: 0;
    margin: 12px -8px 0;
  }

  .btn-wrapper {
    flex-direction: column;
    gap: 12px;
    padding-top: 16px;
    padding-bottom: 20px;
  }

  .edit-btn,
  .close-btn {
    width: 100%;
    height: 44px;
    font-size: 16px;
    border-radius: 6px;
  }

  /* Modal sizing for mobile */
  ::v-deep .big-modal {
    width: 95% !important;
    height: 90% !important;
    left: 2.5% !important;
    top: 5% !important;
    overflow: auto;
    max-height: 90vh;
  }

  ::v-deep .small-modal {
    width: 95% !important;
    left: 2.5% !important;
    top: 5% !important;
    max-height: 90vh;
    overflow: auto;
  }

  /* Ensure proper modal positioning */
  ::v-deep .vm--modal {
    position: fixed !important;
    top: 5% !important;
    left: 2.5% !important;
    right: 2.5% !important;
    bottom: 5% !important;
    width: 95% !important;
    height: 90% !important;
    transform: none !important;
  }

  /* Ensure modal content is properly positioned */
  ::v-deep .vm--modal .vm--modal-box {
    position: relative !important;
    width: 100% !important;
    height: 100% !important;
    max-height: 100% !important;
    overflow: hidden !important;
  }

  /* Ensure modal content is scrollable */
  ::v-deep .vm--modal .vm--modal-box .vm--modal-content {
    height: 100% !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
}

@media (max-width: 480px) {
  .room-wrapper {
    margin: 70px 12px 0;
    width: calc(100% - 24px);
    padding: 0 6px;
  }

  .room-header {
    margin-bottom: 14px;
  }

  .room-meta {
    gap: 10px;
    margin-bottom: 16px;
  }

  .room-internal-name {
    font-size: 16px;
    line-height: 22px;
    margin-bottom: 4px;
  }

  .room-marketing-name {
    font-size: 13px;
    line-height: 16px;
  }

  .room-property-internal-name {
    font-size: 13px;
    gap: 8px;
  }

  .home-icon {
    width: 14px;
    height: 14px;
  }

  .sub-title {
    font-size: 13px;
  }

  .price-info {
    font-size: 13px;
    gap: 8px;
  }

  .estimated-rent-legend,
  .last-year-rent-legend,
  .actual-rent-legend,
  .average-rent-legend {
    font-size: 10px;
    padding: 8px;
    width: 70px;
  }

  .modal-footer {
    margin: 10px -6px 0;
  }

  .edit-btn,
  .close-btn {
    height: 40px;
    font-size: 14px;
  }

  /* Modal sizing for small mobile */
  ::v-deep .big-modal,
  ::v-deep .small-modal {
    width: 98% !important;
    left: 1% !important;
    top: 2% !important;
    max-height: 96vh;
  }

  /* Ensure proper modal positioning for small mobile */
  ::v-deep .vm--modal {
    position: fixed !important;
    top: 2% !important;
    left: 1% !important;
    right: 1% !important;
    bottom: 2% !important;
    width: 98% !important;
    height: 96% !important;
    transform: none !important;
  }

  /* Ensure modal content is properly positioned */
  ::v-deep .vm--modal .vm--modal-box {
    position: relative !important;
    width: 100% !important;
    height: 100% !important;
    max-height: 100% !important;
    overflow: hidden !important;
  }

  /* Ensure modal content is scrollable */
  ::v-deep .vm--modal .vm--modal-box .vm--modal-content {
    height: 100% !important;
    overflow-y: auto !important;
    -webkit-overflow-scrolling: touch;
  }
}
</style>
